import { useEffect, useState } from 'react'
import { passkeysSupported, platformAuthenticatorAvailable, register, signIn, signInWithPin } from '../lib/auth'
import { t } from '../lib/i18n'

/**
 * Sign in, or create an account.
 *
 * There is no password field and no "forgot password" link, because there is no
 * password. That is stated on the screen rather than left for someone to
 * discover by looking for the missing control.
 *
 * ── The third mode ──────────────────────────────────────────────────────────
 *
 * `pin` signs in a DELEGATE — an account a guardian made, which holds no
 * capabilities and forgets after seven days. It is deliberately the least
 * prominent thing here: a small link under the passkey button, not a tab.
 *
 * It is not decoration. Both stores require a reviewer to be able to sign in,
 * and a reviewer cannot enrol a passkey on our authenticator. `/api/auth/pin`
 * existed and worked for months while this control did not, so the App Review
 * notes told reviewers to press a button that was not on the screen — which
 * would have been a guideline 2.1 rejection nobody could have explained.
 */
export default function SignIn({ onSignedIn }: { onSignedIn: () => Promise<void> }) {
  const [mode, setMode] = useState<'in' | 'up' | 'pin'>('in')
  const [handle, setHandle] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [setupToken, setSetupToken] = useState('')
  const [showToken, setShowToken] = useState(false)
  const [pin, setPin] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Whether WebAuthn exists at all decides what is shown; whether there is a
  // BUILT-IN authenticator only decides how the hint is worded.
  const supported = passkeysSupported()
  const [hasPlatform, setHasPlatform] = useState<boolean | null>(null)

  useEffect(() => {
    void platformAuthenticatorAvailable().then(setHasPlatform)
  }, [])

  async function run(fn: () => Promise<unknown>) {
    setBusy(true)
    setError(null)
    try {
      await fn()
      await onSignedIn()
    } catch (err) {
      /*
       * A cancelled ceremony is not an error worth alarming anyone about — the
       * user dismissed a system sheet. Everything else gets the real message.
       */
      const name = (err as { name?: string }).name
      if (name === 'NotAllowedError' || name === 'AbortError') setError(t('auth.cancelled'))
      else setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  /*
   * No WebAuthn at all still leaves the PIN door open.
   *
   * Returning early here used to end the screen, which meant a browser without
   * passkeys had no way in whatsoever — including the one case this mode is for.
   */
  if (!supported && mode !== 'pin') {
    return (
      <section className="card">
        <p>{t('auth.noPasskeySupport')}</p>
        <button className="link" onClick={() => setMode('pin')}>{t('auth.usePin')}</button>
      </section>
    )
  }

  return (
    <section className="card">
      <h1>{mode === 'up' ? t('auth.createAccount') : mode === 'pin' ? t('auth.pinTitle') : t('app.signIn')}</h1>

      <label>
        {t('auth.handle')}
        <input
          value={handle}
          onChange={(e) => setHandle(e.target.value.toLowerCase())}
          autoComplete="username webauthn"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          placeholder="yourname"
        />
      </label>

      {mode === 'up' && (
        <>
          <label>
            {t('auth.displayName')}
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} autoComplete="name" />
          </label>
          {/*
            * Hidden until asked for. Shown as a third field on every sign-up it
            * read as required, and people stopped there — almost nobody has a
            * setup token, because almost nobody is the owner.
            */}
          {showToken ? (
            <label>
              {t('auth.setupToken')} <span className="muted small">— only for the owner account</span>
              <input value={setupToken} onChange={(e) => setSetupToken(e.target.value)} autoComplete="off" />
            </label>
          ) : (
            <p className="muted small">
              <button type="button" className="link" onClick={() => setShowToken(true)}>I have a setup token</button>
            </p>
          )}
        </>
      )}

      {mode === 'pin' && (
        <label>
          {t('auth.pin')}
          <input
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            /* Digits only, and a numeric keypad on a phone. `type=password`
               rather than `number`: it must not render spinners, and it should
               be masked on a shared screen. */
            type="password"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={12}
            placeholder="••••••"
          />
        </label>
      )}

      <button
        className="primary"
        disabled={busy || (mode === 'up' && !handle) || (mode === 'pin' && (!handle || pin.length < 4))}
        onClick={() =>
          run(() =>
            mode === 'in'
              ? signIn(handle || undefined)
              : mode === 'pin'
                ? signInWithPin(handle, pin)
                : register({ handle, displayName, setupToken: setupToken || undefined }),
          )
        }
      >
        {busy ? '…' : mode === 'pin' ? t('app.signIn') : mode === 'in' ? t('auth.usePasskey') : t('auth.createAccount')}
      </button>

      {error && <p className="error" role="alert">{error}</p>}

      {/*
        * Three modes need three different ways back, and the PIN screen needs
        * its own wording: "Already have one? Sign in" is nonsense there, and it
        * rendered a second control also labelled "Sign in" beside the submit
        * button — ambiguous to a screen reader and to a test.
        */}
      <p className="muted small">
        {mode === 'in' && (
          <>No account? <button className="link" onClick={() => setMode('up')}>{t('auth.createAccount')}</button></>
        )}
        {mode === 'up' && (
          <>Already have one? <button className="link" onClick={() => setMode('in')}>{t('app.signIn')}</button></>
        )}
        {mode === 'pin' && (
          <button className="link" onClick={() => { setMode('in'); setPin('') }}>{t('auth.backToPasskey')}</button>
        )}
      </p>

      {/*
        * Deliberately quiet, and only offered from the sign-in screen.
        *
        * Almost nobody should take this door — it is for a shared device and
        * for a store reviewer. Making it a tab beside "passkey" would suggest
        * a choice where there is not one.
        */}
      {mode === 'in' && (
        <p className="muted small">
          <button className="link" onClick={() => setMode('pin')}>{t('auth.usePin')}</button>
        </p>
      )}

      <p className="muted small">
        {mode === 'pin'
          ? t('auth.pinHint')
          : <>There is no password. {hasPlatform === false
              ? 'Use a security key, your password manager, or your phone.'
              : 'Your passkey — Face ID, a fingerprint, your password manager or a security key — is the only way in.'}</>}
      </p>
    </section>
  )
}
